/**
 * Types: BROWSE-V1 feature
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R03, BROWSE-V1-R04, BROWSE-V1-R05,
 *               BROWSE-V1-R07, BROWSE-V1-R08
 *
 * Description: Frontend type definitions for the BROWSE-V1 list page
 *              and guideline viewer page. Mirrors the backend
 *              `BrowseGuidelineListItem` and `BrowseGuidelineDetail`
 *              Pydantic schemas in camelCase (which is what the API
 *              already serialises thanks to the `by_alias=True`
 *              response model on the `GET /api/v1/browse/guidelines`
 *              and `GET /api/v1/browse/guidelines/{id}` endpoints).
 *
 *              `GuidelineDetail.fullContent` stays typed as the
 *              untrusted `Record<string, unknown>` the backend
 *              forwards verbatim. The `SectionContentEntry` type
 *              here documents the shape we *expect* inside
 *              `fullContent.sectionContent[]`, and
 *              `extractSectionContent` is the narrowing helper the
 *              viewer uses to read it defensively (missing field,
 *              non-array, malformed entries all surface as []).
 *
 * Risk Links: R9
 * FMEA Links: FM9
 */

/**
 * One row of `GET /api/v1/browse/guidelines`.
 *
 * The backend sorts by `guidelineName` ascending and does not paginate;
 * the store holds ~30 rows and the list view renders them all at once
 * per BROWSE-V1-R09.
 */
export interface GuidelineListItem {
  guidelineId: number;
  guidelineName: string;
  /** ISO timestamp from upstream `/Index` publishDate. */
  publishDate: string;
  /** ISO timestamp from upstream `/Index` updateDate. */
  updateDate: string;
  /**
   * Optional external link to the upstream guideline page
   * (e.g. an academic journal URL). BROWSE-V1-R03 renders this as an
   * external-link icon only when present.
   */
  externalGuidelineURL: string | null;
}

/**
 * Full guideline detail returned by `GET /api/v1/browse/guidelines/{id}`.
 *
 * BROWSE-V1-R04: The metadata fields (guidelineId..externalGuidelineURL)
 * are duplicated outside `fullContent` so the viewer header can render
 * without narrowing into the JSONB blob. The `fullContent` field is
 * forwarded verbatim from the upstream `/Get` payload; the viewer
 * must reach into it only for presentation (R05 sectionContent render,
 * R06 TOC build, R07 colour coding, R08 citation popovers) and must
 * not re-serve it via any path that bypasses this client (R11).
 *
 * Typed as `Record<string, unknown>` so later requirements can narrow
 * specific fields (`sectionContent`, `recommendationColourOption`,
 * etc.) inside the components that actually touch them, without this
 * file needing to enumerate every upstream field upfront.
 */
export interface GuidelineDetail {
  guidelineId: number;
  guidelineName: string;
  shortTitle: string;
  publishDate: string;
  updateDate: string;
  externalGuidelineURL: string | null;
  /**
   * Full upstream `/Get` payload, verbatim. The backend stores it as
   * JSONB in the local cache and returns it unchanged so the frontend
   * renderer consumes the upstream shape directly.
   */
  fullContent: Record<string, unknown>;
}

/**
 * Single section entry inside `fullContent.sectionContent[]`.
 *
 * BROWSE-V1-R05: The upstream `/Get` payload emits sectionContent as
 * an array of section-structured entries; the viewer renders each
 * one in source order. Only the fields R05 actively uses are pinned
 * here (id, hierarchy, name, HTML body, optional recommendation
 * children the later phases will narrow).
 *
 * This type is **expected shape**, not **guaranteed shape** — the
 * API returns `Record<string, unknown>` from `GuidelineDetail.fullContent`
 * and the viewer always goes through `extractSectionContent()` below
 * to turn it into a safe array. Broken / missing entries surface as
 * a filtered-out empty array rather than a runtime crash.
 */
export interface SectionContentEntry {
  sectionId: number;
  sectionHierarchy: number;
  sectionName: string;
  /** Sanitised and rendered by SafeHtml at the consumer. */
  sectionText: string;
  /**
   * Raw recommendation entries from the upstream payload. Narrowed
   * by `extractRecommendationsFromSection` at the consumer into
   * `RecommendationContentEntry[]`; malformed entries are dropped.
   */
  recommendationContent?: unknown[];
}

/**
 * BROWSE-V1-R07: One recommendation inside a section's
 * `recommendationContent[]`. The upstream payload carries the
 * strength and evidence-strength colour values verbatim (as `colour`
 * fields on nested objects), which RecommendationCard applies via
 * CSS custom properties. No CONFIG-V1 colour map lookup — all
 * colour data is self-contained in the guideline payload per the
 * implementation plan.
 */
export interface RecommendationContentEntry {
  recommendationId: number;
  /** Sanitised and rendered by SafeHtml at the consumer. */
  recommendationText: string;
  strength: {
    label: string;
    colour: string;
  };
  evidenceStrength: {
    label: string;
    colour: string;
  };
}

/**
 * BROWSE-V1-R07: Defensive narrowing for a section's
 * recommendation list. Drops entries missing any required field
 * (`recommendationId`, `recommendationText`, `strength.label`,
 * `strength.colour`, `evidenceStrength.label`, `evidenceStrength.colour`)
 * so a broken upstream entry can't crash the viewer.
 */
export function extractRecommendationsFromSection(
  section: SectionContentEntry,
): RecommendationContentEntry[] {
  const raw = section.recommendationContent;
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: RecommendationContentEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    // Upstream uses 'id'; map to our interface.
    const rawId = e['recommendationId'] ?? e['id'];
    const rawText = e['recommendationText'];
    const rawStrength = e['strength'];
    const rawEvidence = e['evidenceStrength'];
    const id = typeof rawId === 'number' ? rawId : null;
    const text = typeof rawText === 'string' ? rawText : null;
    const strength = narrowColouredLabel(rawStrength);
    const evidence = narrowColouredLabel(rawEvidence);
    if (id === null || text === null || !strength || !evidence) {
      continue;
    }
    narrowed.push({
      recommendationId: id,
      recommendationText: text,
      strength,
      evidenceStrength: evidence,
    });
  }
  return narrowed;
}

function narrowColouredLabel(
  raw: unknown,
): { label: string; colour: string } | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  // Upstream uses 'description'; map to our interface.
  const label = o['label'] ?? o['description'];
  const colour = o['colour'];
  if (typeof label !== 'string' || typeof colour !== 'string') {
    return null;
  }
  return { label, colour };
}

/**
 * BROWSE-V1-R07: Read the top-level `recommendationColourOption`
 * field from a GuidelineDetail. The upstream contract uses this as
 * a mode selector; the viewer forwards it to each
 * `RecommendationCard` so every card in a guideline renders under
 * the same mode. Missing or non-string values return `null`, which
 * RecommendationCard treats as "no mode specified" and skips the
 * mode-scoped className.
 */
export function extractRecommendationColourOption(
  detail: GuidelineDetail,
): string | null {
  const raw = detail.fullContent?.['recommendationColourOption'];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * BROWSE-V1-R08: A single reference from the upstream
 * `fullContent.referenceContent[]` array. Inline citation anchors
 * (`<a href="#reference-N">` or `<a data-reference-id="N">`) inside
 * sanitised `sectionText` are wired by `SectionContent` to open a
 * `ReferencePopover` with the matching entry.
 *
 * `referenceId` can be number or string in upstream payloads; the
 * narrowing helper coerces to string for lookup consistency. Only
 * `referenceId` and `referenceText` are required — everything else
 * is displayed when present but never gates rendering.
 */
export interface ReferenceEntry {
  referenceId: string;
  referenceText: string;
  url?: string;
  /** Inline HTML from upstream data-content popover attributes. */
  html?: string;
}

/**
 * BROWSE-V1-R08: Defensive narrowing for
 * `fullContent.referenceContent`. Drops entries missing the two
 * required fields silently so a broken upstream entry can't crash
 * the viewer. Returns `[]` when the key is missing or not an array.
 */
export function extractReferenceContent(
  detail: GuidelineDetail,
): ReferenceEntry[] {
  const raw = detail.fullContent?.['referenceContent'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: ReferenceEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const rawId = e['referenceId'];
    const rawText = e['referenceText'];
    const rawUrl = e['url'];
    const referenceId =
      typeof rawId === 'number'
        ? String(rawId)
        : typeof rawId === 'string' && rawId.length > 0
          ? rawId
          : null;
    const referenceText = typeof rawText === 'string' ? rawText : null;
    if (referenceId === null || referenceText === null) {
      continue;
    }
    narrowed.push({
      referenceId,
      referenceText,
      url: typeof rawUrl === 'string' && rawUrl.length > 0 ? rawUrl : undefined,
    });
  }
  return narrowed;
}

/**
 * BROWSE-V1-R08: Build a Map keyed by string `referenceId` so the
 * popover click handler in `SectionContent` can resolve citation
 * anchors in O(1). The map is built once per guideline payload by
 * `ViewerPage` and threaded down through `SectionContent` props.
 */
export function buildReferenceLookup(
  refs: ReferenceEntry[],
): Map<string, ReferenceEntry> {
  const lookup = new Map<string, ReferenceEntry>();
  for (const ref of refs) {
    lookup.set(ref.referenceId, ref);
  }
  return lookup;
}

/**
 * Defensive narrowing helper for `GuidelineDetail.fullContent.sectionContent`.
 *
 * Returns an array of `SectionContentEntry` where every required field
 * is present and the right type. Drops malformed entries silently
 * (rather than crashing the viewer) and returns `[]` when the
 * `sectionContent` key is missing or not an array.
 *
 * BROWSE-V1-R05: Preserves input order — `Array.prototype.filter`
 * does not re-order, and the consumer must never call `sort` on the
 * result. The upstream `/Get` payload already orders sections the
 * way they should render, and the requirement calls this out
 * explicitly ("in the order supplied by the source data").
 */
export function extractSectionContent(
  detail: GuidelineDetail,
): SectionContentEntry[] {
  const raw = detail.fullContent?.['sectionContent'];
  if (!Array.isArray(raw)) {
    return [];
  }
  const narrowed: SectionContentEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    // Upstream uses 'id'/'name'/'sectionSortOrder'; map to our interface.
    const rawId = e['sectionId'] ?? e['id'];
    const rawName = e['sectionName'] ?? e['name'];
    const rawHierarchy = e['sectionHierarchy'];
    const rawSortOrder = e['sectionSortOrder'];
    const rawText = e['sectionText'];
    const rawRecs = e['recommendationContent'];
    const sectionId = typeof rawId === 'number' ? rawId : null;
    // Derive hierarchy from sectionSortOrder dot-depth (e.g. "9.6.5" → 3)
    // when sectionHierarchy is absent.
    const sectionHierarchy =
      typeof rawHierarchy === 'number'
        ? rawHierarchy
        : typeof rawSortOrder === 'string'
          ? rawSortOrder.split('.').length
          : null;
    const sectionName = typeof rawName === 'string' ? rawName : null;
    const sectionText = typeof rawText === 'string' ? rawText : '';
    if (sectionId === null || sectionHierarchy === null || sectionName === null) {
      continue;
    }
    narrowed.push({
      sectionId,
      sectionHierarchy,
      sectionName,
      sectionText,
      recommendationContent: Array.isArray(rawRecs) ? (rawRecs as unknown[]) : undefined,
    });
  }
  return narrowed;
}
